import { useEffect } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { Dropdown, Menu, Row, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { ExternalAccess } from '../interfaces';
import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { commonOptions } from '../../../config/commons';
import { LrvTag } from '../../../common/components/LrvTag/LrvTag';
import { LrvTable } from '../../../common/components/LrvTable/LrvTable';
import ActionButton from '../../../common/components/buttons/ActionButton';
import { ExtraActionsButton } from '../../../common/components/buttons/ExtraActionsButton';

import styles from './TabExternalAccess.module.scss';
import { NewExternalAccess } from './NewExternalAccess';
import * as externalAccessSlice from './externalAccessSlice';
import { EditExternalAccess } from './EditExternalAccess';
import * as integrationAQ1Slice from './integrationAQ1Slice';
import { TankIntegrationForm } from './TankIntegrationForm';

interface Props {
  theme?: 'dark' | 'light';
}

export const TabExternalAccess = (props: Props) => {
  const { theme } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { externalAccess, isLoading } = useSelector((state: Store) => state.externalAccess);
  
  useEffect(() => {
    dispatch(externalAccessSlice.fetchExternalAccess());
  }, [dispatch]);

  const menuItemsOptions = (item: ExternalAccess) => {
    return (
      <Menu
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();

          switch (key) {
            case commonOptions.EDIT:
              dispatch(externalAccessSlice.setSelectedExternalAccess(item));
              dispatch(externalAccessSlice.setShowEditModal(true));
              break;
          }
        }}
      >
        <Menu.Item id='menu_option_edit' key={commonOptions.EDIT} className={styles.menuItemOptions}>
          <Icon name='edit' theme={theme} className={styles.icon} />
          <span>{t('actions.edit')}</span>
        </Menu.Item>
      </Menu>
    );
  };

  const renderDropdownOptions = (item: ExternalAccess) => {
    return (
      <Dropdown
        overlay={() => menuItemsOptions(item)}
        trigger={['click']}
        placement='bottomRight'
      >
        <ExtraActionsButton
          onClick={(e) => e.stopPropagation()}
          id={`button_${item._id}`}
        />
      </Dropdown>
    );
  };

  const renderStatusTag = (record: ExternalAccess) => {
    if (record.active) {
      // if return null, table will display an "-" in the empty cell
      return <div></div>;
    }

    const id = `tag_${record._id}`;
    return (
      <LrvTag
        id={id}
        type='info'
      >
        {t('integrations.inactive')}
      </LrvTag>
    );
  };

  const columns: ColumnsType<ExternalAccess> = [
    {
      key: 1,
      title: `${t('integrations.apiConfig.name')}`,
      dataIndex: 'apiConfig',
      width: '25%',
      render: (value) => value?.name || '-',
    },
    {
      key: 2,
      title: `${t('integrations.apiConfig.user')} ${t('integrations.api')}`,
      dataIndex: 'apiConfig',
      width: '25%',
      render: (value) => value?.user || '-',
    },
    {
      key: 3,
      title: t('integrations.apiConfig.url'),
      dataIndex: 'apiConfig',
      width: '30%',
      render: (value) => value?.url || '-',
    },
    {
      key: 4,
      dataIndex: 'active',
      width: '12%',
      render: (_, record: ExternalAccess) => {
        return renderStatusTag(record);
      },
    },
    {
      key: 5,
      width: '5%',
      render: (_, record: ExternalAccess) => {
        return (
          <div>
            {renderDropdownOptions(record)}
          </div>
        );
      },
    }
  ];

  return (
    <div className={styles.container}>
      <Space direction='vertical'>
        <Row justify='end' align='middle'>
          <Space>
            {
              externalAccess.length > 0 &&
              <ActionButton
                type='primary'
                className={styles.actionButton}
                onClick={() => {
                  dispatch(integrationAQ1Slice.setShowModal(true));
                  dispatch(integrationAQ1Slice.fetchCampuses());
                }}
              >
                {t('integrations.poolMapping.title')}
              </ActionButton>
            }

            <ActionButton
              id='new_button'
              type='primary'
              className={styles.actionButton}
              icon={<Icon name='add' />}
              onClick={() => {
                dispatch(externalAccessSlice.setShowCreateModal(true));
              }}
            >
              {t('integrations.title')}
            </ActionButton>
          </Space>
        </Row>

        <Row>
          <LrvTable
            columns={columns}
            dataSource={externalAccess}
            loading={isLoading}
            scroll={{ y: '' }}
            theme={theme}
            size='small'
            pagination={{
              size: 'default',
            }}
          />
        </Row>
      </Space>

      <NewExternalAccess theme='light' />
      <EditExternalAccess theme='light' />
      <TankIntegrationForm theme='light' />
    </div>
  );
};
